<!-- SwipFx Market Depth Page (Bootstrap 5) -->
<!-- Assumes Bootstrap CSS + bootstrap.bundle.js are included -->
<div class="min-vh-100 bg-light">

  <app-header [page]="'Market'"></app-header>

  <main class="container py-4">

    <!-- TOP BAR -->
    <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
      <div>
        <h4 class="mb-0 fw-bold">Market Depth</h4>
        <div class="text-muted small">Order book, spread, and recent trades</div>
      </div>

      <div class="d-flex flex-wrap gap-2 align-items-center">
        <div class="input-group input-group-sm" style="width: 240px;">
          <span class="input-group-text">Pair</span>
          <select class="form-select" [(ngModel)]="selectedPair" (change)="onPairChanged()">
            <option *ngFor="let p of pairOptions" [value]="p">{{ p }}</option>
            <option *ngIf="pairOptions.length === 0" disabled selected>No pairs</option>
          </select>
        </div>

        <div class="input-group input-group-sm" style="width: 180px;">
          <span class="input-group-text">Depth</span>
          <select class="form-select" [(ngModel)]="depth" (change)="loadMarket()">
            <option *ngFor="let d of depthOptions" [value]="d">{{ d }}</option>
          </select>
        </div>

        <button class="btn btn-outline-secondary btn-sm" (click)="refreshMarket()" [disabled]="marketLoading">
          <span *ngIf="!marketLoading">Refresh</span>
          <span *ngIf="marketLoading" class="spinner-border spinner-border-sm"></span>
        </button>
      </div>
    </div>

    <!-- SUMMARY STRIP -->
    <div class="row g-3 mb-3">
      <div class="col-12">
        <div class="card shadow-sm border-0">
          <div class="card-body py-3">
            <div class="row g-3 align-items-center">
              <div class="col-12 col-md-4">
                <div class="text-muted small">Last Price</div>
                <div class="d-flex align-items-baseline gap-2">
                  <div class="fs-5 fw-bold">{{ market?.summary?.lastPrice ?? 0 | smartAmount }}</div>
                  <div class="small" [ngClass]="changeClass(market?.summary?.change24hPercent)">
                    {{ market?.summary?.change24hPercent ?? 0 | number:'1.2-2' }}%
                  </div>
                </div>
              </div>

              <div class="col-6 col-md-2">
                <div class="text-muted small">Bid</div>
                <div class="fw-semibold">{{ market?.summary?.bestBid ?? 0 | smartAmount }}</div>
              </div>
              <div class="col-6 col-md-2">
                <div class="text-muted small">Ask</div>
                <div class="fw-semibold">{{ market?.summary?.bestAsk ?? 0 | smartAmount }}</div>
              </div>

              <div class="col-6 col-md-2">
                <div class="text-muted small">Spread</div>
                <div class="fw-semibold">{{ market?.summary?.spread ?? 0 | smartAmount }}</div>
              </div>

              <div class="col-6 col-md-2">
                <div class="text-muted small">24h Volume</div>
                <div class="fw-semibold">{{ market?.summary?.volume24h ?? 0 | smartAmount }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MAIN GRID -->
    <div class="row g-3">

      <!-- LEFT: ORDER BOOK -->
      <div class="col-12 col-lg-7">
        <div class="card shadow-sm border-0 h-100">
          <div class="card-body p-0">

            <div class="p-3 border-bottom d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <div class="fw-bold">Order Book</div>
                <div class="text-muted small">Top-of-book depth (Asks & Bids)</div>
              </div>

              <div class="d-flex gap-2">
                <div class="input-group input-group-sm" style="width: 200px;">
                  <span class="input-group-text">Group</span>
                  <select class="form-select">
                    <option selected>0.25</option>
                    <option>0.50</option>
                    <option>1.00</option>
                    <option>5.00</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="p-3">
              <div class="row g-3">
                <!-- ASKS -->
                <div class="col-12 col-md-6">
                  <div class="d-flex align-items-center justify-content-between mb-2">
                    <div class="fw-semibold text-danger">Asks (Sell)</div>
                    <div class="text-muted small">Price / Amount / Total</div>
                  </div>

                  <div class="border rounded-3 bg-white overflow-hidden">
                    <div class="px-3 py-2 border-bottom small text-muted d-flex justify-content-between">
                      <span>Price</span><span>Amount</span><span>Total</span>
                    </div>

                    <!-- Row -->
                     <div class="px-3 py-2 border-bottom d-flex justify-content-between align-items-center position-relative"
                        *ngFor="let row of asks">
                      <div class="position-absolute top-0 bottom-0 end-0 bg-danger bg-opacity-10"
                          [style.width]="askBarWidth(row)"></div>

                      <span class="text-danger fw-semibold">{{ row.price | smartAmount }}</span>
                      <span>{{ row.amount | smartAmount }}</span>
                      <span class="text-muted">{{ row.total | smartAmount }}</span>
                    </div>
                  </div>
                </div>

                <!-- BIDS -->
                <div class="col-12 col-md-6">
                  <div class="d-flex align-items-center justify-content-between mb-2">
                    <div class="fw-semibold text-success">Bids (Buy)</div>
                    <div class="text-muted small">Price / Amount / Total</div>
                  </div>

                  <div class="border rounded-3 bg-white overflow-hidden">
                    <div class="px-3 py-2 border-bottom small text-muted d-flex justify-content-between">
                      <span>Price</span><span>Amount</span><span>Total</span>
                    </div>
                    
                    <div class="px-3 py-2 border-bottom d-flex justify-content-between align-items-center position-relative"
                        *ngFor="let row of bids">
                      <div class="position-absolute top-0 bottom-0 start-0 bg-success bg-opacity-10"
                          [style.width]="bidBarWidth(row)"></div>

                      <span class="text-success fw-semibold">{{ row.price | smartAmount }}</span>
                      <span>{{ row.amount | smartAmount }}</span>
                      <span class="text-muted">{{ row.total | smartAmount }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- MID-PRICE STRIP -->
              <div
                class="mt-3 p-3 rounded-3 border bg-white d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div class="text-muted small">Mid Price</div>
                <div class="fw-bold">
                  {{
                    ((market?.summary?.bestBid ?? 0) + (market?.summary?.bestAsk ?? 0)) / 2
                    | smartAmount
                  }}
                </div>
                <div class="text-muted small">Spread</div>
                <div class="fw-semibold">
                  <ng-container *ngIf="market?.summary?.bestAsk != null && market?.summary?.bestBid != null; else noSpread">
                    {{ (market!.summary!.bestAsk! - market!.summary!.bestBid!) | smartAmount }}
                  </ng-container>
                  <ng-template #noSpread>—</ng-template>
                </div>
                <div class="text-muted small">Top Size</div>
                <div class="fw-semibold">
                  Ask {{ asks[0]?.amount ?? 0 | smartAmount }} • Bid {{ bids[0]?.amount ?? 0 | smartAmount }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT: CHART + TRADES + QUICK ORDER -->
      <div class="col-12 col-lg-5">
        <div class="row g-3">
          <!-- CHART PLACEHOLDER -->
          <div class="col-12">
            <div class="card shadow-sm border-0">
              <div class="card-body">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <div>
                    <div class="fw-bold">Price Chart</div>
                    <div class="text-muted small">Short-term movement</div>
                  </div>
                  <div class="btn-group btn-group-sm" role="group" aria-label="Timeframe">
                    <button type="button" class="btn"
                      [ngClass]="selectedTimeframe==='1H' ? 'btn-secondary' : 'btn-outline-secondary'"
                      (click)="setTimeframe('1H')">1H</button>

                    <button type="button" class="btn"
                      [ngClass]="selectedTimeframe==='4H' ? 'btn-secondary' : 'btn-outline-secondary'"
                      (click)="setTimeframe('4H')">4H</button>

                    <button type="button" class="btn"
                      [ngClass]="selectedTimeframe==='1D' ? 'btn-secondary' : 'btn-outline-secondary'"
                      (click)="setTimeframe('1D')">1D</button>

                    <button type="button" class="btn"
                      [ngClass]="selectedTimeframe==='1W' ? 'btn-secondary' : 'btn-outline-secondary'"
                      (click)="setTimeframe('1W')">1W</button>
                  </div>
                </div>

                <div class="rounded-3 border bg-white d-flex align-items-center justify-content-center"
                  style="height: 220px;">
                  <div class="rounded-3 border bg-white p-2" style="height: 220px;">
                    <canvas #priceChart></canvas>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- RECENT TRADES -->
          <div class="col-12">
            <div class="card shadow-sm border-0">
              <div class="card-body p-0">
                <div class="p-3 border-bottom">
                  <div class="fw-bold">Recent Trades</div>
                  <div class="text-muted small">Latest executions on this pair</div>
                </div>

                <div class="table-responsive">
                  <table class="table table-sm mb-0 align-middle">
                    <thead class="table-light">
                      <tr class="small text-muted">
                        <th class="ps-3">Time</th>
                        <th>Price</th>
                        <th class="text-end pe-3">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let t of trades">
                        <td class="ps-3 small text-muted">{{ t.time | date:'HH:mm:ss' }}</td>
                        <td [class]="tradePriceClass(t.side)">{{ t.price | smartAmount }}</td>
                        <td class="text-end pe-3">{{ t.amount | smartAmount }}</td>
                      </tr>

                      <tr *ngIf="trades.length === 0">
                        <td colspan="3" class="text-center text-muted small py-3">No trades yet</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

              </div>
            </div>
          </div>

          <!-- QUICK ORDER (OPTIONAL) -->
          <div class="col-12">
            <app-order-entry
              title="Quick Trade"
              [currencies]="currencies"
              [fee]="fee"
              [loading]="loading"
              [initialDraft]="quickTradeDraft">
            </app-order-entry>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  
  </main>
</div>

<script>
  document.getElementById('yr').textContent = new Date().getFullYear();
</script>
